import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  Input,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../contexts/ApiContext';
import { useThemeContext } from '../../../contexts/ThemeContext';
import {
  CordeCommonModelsDtoUserprofileUpdateUISettingsRequest as UpdateUISettingsRequest,
} from '../../../api/generated/v1/api';
import { UserProfile } from '../../../contexts/userprofile/UserProfileContext';

interface UISettingsProps {
  userId: string;
  profile: UserProfile | null;
  onMarkChanged: (hasChanges: boolean) => void;
  onClearChanges: () => void;
}

interface UIFormData {
  theme: string;
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  compactMode: boolean;
  animations: boolean;
  customSettings: { [key: string]: string };
}

export const UISettings: React.FC<UISettingsProps> = ({
  userId,
  profile,
  onMarkChanged,
  onClearChanges,
}) => {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const api = useApi();
  const toast = useToast();
  const { colorMode, toggleColorMode } = useThemeContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<UIFormData>({
    theme: 'auto',
    language: 'ru',
    timezone: 'UTC',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    compactMode: false,
    animations: true,
    customSettings: {},
  });
  const [originalData, setOriginalData] = useState<UIFormData>({
    theme: 'auto',
    language: 'ru',
    timezone: 'UTC',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    compactMode: false,
    animations: true,
    customSettings: {},
  });

  // Initialize form data from profile
  useEffect(() => {
    if (profile?.ui_settings) {
      const settings = profile.ui_settings as any;
      const data: UIFormData = {
        theme: settings.settings?.theme || colorMode || 'auto',
        language: settings.settings?.language || i18n.language || 'ru',
        timezone: settings.settings?.timezone || 'UTC',
        dateFormat: settings.settings?.dateFormat || 'DD.MM.YYYY',
        timeFormat: settings.settings?.timeFormat || '24h',
        compactMode: settings.settings?.compactMode === 'true' || false,
        animations: settings.settings?.animations !== 'false',
        customSettings: { ...settings.settings } || {},
      };
      setFormData(data);
      setOriginalData(data);
    } else {
      // Load UI settings from API if not available in profile
      loadUISettings();
    }
  }, [profile, colorMode, i18n.language]);

  // Check for changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    onMarkChanged(hasChanges);
  }, [formData, originalData, onMarkChanged]);

  const loadUISettings = async () => {
    if (!profile?.id) return;
    
    setIsLoading(true);
    try {
      const response = await api.UserprofileApi.getUISettings(profile.id);
      const settings = response.data.settings;
      
      if (settings?.settings) {
        const data: UIFormData = {
          theme: settings.settings.theme || colorMode || 'auto',
          language: settings.settings.language || i18n.language || 'ru',
          timezone: settings.settings.timezone || 'UTC',
          dateFormat: settings.settings.dateFormat || 'DD.MM.YYYY',
          timeFormat: settings.settings.timeFormat || '24h',
          compactMode: settings.settings.compactMode === 'true' || false,
          animations: settings.settings.animations !== 'false',
          customSettings: { ...settings.settings } || {},
        };
        setFormData(data);
        setOriginalData(data);
      }
    } catch (error: any) {
      console.error('Failed to load UI settings:', error);
      // Use defaults if loading fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof Omit<UIFormData, 'customSettings'>, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile?.id) {
      toast({
        title: t('settings.ui.noProfileError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const updateRequest: UpdateUISettingsRequest = {
        profile_id: profile.id,
        settings: {
          theme: formData.theme,
          language: formData.language,
          timezone: formData.timezone,
          dateFormat: formData.dateFormat,
          timeFormat: formData.timeFormat,
          compactMode: formData.compactMode.toString(),
          animations: formData.animations.toString(),
          ...formData.customSettings,
        },
      };

      await api.UserprofileApi.updateUISettings(profile.id, updateRequest);
      
      // Apply theme change immediately
      if (formData.theme !== colorMode && formData.theme !== 'auto') {
        toggleColorMode();
      }
      
      // Apply language change immediately
      if (formData.language !== i18n.language) {
        i18n.changeLanguage(formData.language);
      }
      
      // Update original data
      setOriginalData(formData);
      onClearChanges();
      
      toast({
        title: t('settings.ui.saveSuccess'),
        description: t('settings.ui.saveSuccessMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Failed to save UI settings:', error);
      toast({
        title: t('settings.ui.saveError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  const applyThemePreview = (theme: string) => {
    if (theme !== 'auto' && theme !== colorMode) {
      toggleColorMode();
    }
  };

  if (isLoading) {
    return (
      <VStack spacing={4}>
        <Spinner size="lg" />
        <Text>{t('common.loading')}</Text>
      </VStack>
    );
  }

  if (!profile) {
    return (
      <Alert status="warning">
        <AlertIcon />
        {t('settings.ui.noProfile')}
      </Alert>
    );
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <Heading size="md">{t('settings.ui.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('settings.ui.description')}
        </Text>
      </VStack>

      {/* Theme Settings */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.ui.appearance')}</Heading>
            
            <FormControl>
              <FormLabel>{t('settings.ui.theme')}</FormLabel>
              <Select
                value={formData.theme}
                onChange={(e) => {
                  handleInputChange('theme', e.target.value);
                  applyThemePreview(e.target.value);
                }}
              >
                <option value="auto">{t('settings.ui.themeAuto')}</option>
                <option value="light">{t('settings.ui.themeLight')}</option>
                <option value="dark">{t('settings.ui.themeDark')}</option>
              </Select>
            </FormControl>

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.ui.compactMode')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.ui.compactModeDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.compactMode}
                onChange={(e) => handleInputChange('compactMode', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.ui.animations')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.ui.animationsDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.animations}
                onChange={(e) => handleInputChange('animations', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Language & Regional Settings */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.ui.localization')}</Heading>
            
            <FormControl>
              <FormLabel>{t('settings.ui.language')}</FormLabel>
              <Select
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
              >
                <option value="en">{t('settings.ui.english')}</option>
                <option value="ru">{t('settings.ui.russian')}</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>{t('settings.ui.timezone')}</FormLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                <option value="UTC">UTC</option>
                <option value="Europe/Moscow">{t('settings.ui.moscowTime')}</option>
                <option value="Europe/London">{t('settings.ui.londonTime')}</option>
                <option value="America/New_York">{t('settings.ui.newYorkTime')}</option>
                <option value="America/Los_Angeles">{t('settings.ui.losAngelesTime')}</option>
                <option value="Asia/Tokyo">{t('settings.ui.tokyoTime')}</option>
                <option value="Australia/Sydney">{t('settings.ui.sydneyTime')}</option>
              </Select>
            </FormControl>

            <HStack spacing={4}>
              <FormControl flex={1}>
                <FormLabel>{t('settings.ui.dateFormat')}</FormLabel>
                <Select
                  value={formData.dateFormat}
                  onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                >
                  <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                </Select>
              </FormControl>

              <FormControl flex={1}>
                <FormLabel>{t('settings.ui.timeFormat')}</FormLabel>
                <Select
                  value={formData.timeFormat}
                  onChange={(e) => handleInputChange('timeFormat', e.target.value)}
                >
                  <option value="24h">{t('settings.ui.time24h')}</option>
                  <option value="12h">{t('settings.ui.time12h')}</option>
                </Select>
              </FormControl>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Save Actions */}
      {hasChanges && (
        <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
          <CardBody>
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSubmitting}
              >
                {t('common.reset')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={t('common.saving')}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 